/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { CiEdit } from "react-icons/ci";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import usePermissions from "../../../permissions/permission";
import ErrorBoundary from "../../../common/ErrorBoundary";
import { motion } from "framer-motion";

const ViewUserManagement = () => {
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const API_URL = process.env.REACT_APP_FRONTEND_URL;

  // Initialize all state with proper default values
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [employeeNoFilter, setEmployeeNoFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    user_role: "",
    email: "",
    mobile: "",
    isInternalExternal: "No",
    user_type: "user",
  });

  // Safely handle form updates
  const handleForm = (k, v) => {
    if (typeof k === "string" && k.trim()) {
      setFormData((prev) => ({
        ...prev,
        [k]: v || "",
      }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (!API_URL) {
          console.error("API URL not configured");
          return;
        }

        const res = await fetch(`${API_URL}/v1/hris/user/roles`);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        if (Array.isArray(data)) {
          setRoles(data);
        } else if (data?.success && Array.isArray(data.data)) {
          setRoles(data.data);
        } else {
          setRoles([]);
        }
      } catch (e) {
        console.error("Error fetching roles:", e);
        toast.error("Failed to load roles");
        setRoles([]);
      }
    })();
  }, [API_URL]);

  const fetchUsers = async ({ toPage = page, toLimit = limit } = {}) => {
    setLoading(true);
    try {
      if (!API_URL) throw new Error("API URL not configured");

      // Prepare query params
      const params = new URLSearchParams();

      // page & limit
      params.set("page", String(toPage || 1));
      params.set("limit", String(toLimit || 10));

      // username = employeeNoFilter
      if (employeeNoFilter && employeeNoFilter.trim()) {
        params.set("username", employeeNoFilter.trim());
      } else {
        params.set("username", ""); // maintain empty param
      }

      // name
      params.set("name", ""); // You can bind this later to a name filter if needed

      // role
      if (roleFilter && roleFilter.trim()) {
        params.set("role", roleFilter.trim());
      } else {
        params.set("role", ""); // maintain empty param
      }

      const url = `${API_URL}/v1/hris/user/getAllUsers?${params.toString()}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();

      // ✅ Data handling
      const usersData = Array.isArray(data?.data) ? data.data : [];
      setUsers(usersData);

      const totalCount = usersData.length;
      setTotal(totalCount);

      const apiTotalPages =
        typeof toLimit === "number" && toLimit > 0
          ? Math.max(1, Math.ceil(totalCount / toLimit))
          : 1;

      setTotalPages(apiTotalPages);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error(err.message || "Error loading users");
      setUsers([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };



  // initial load
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  const handlePageChange = async (p) => {
    setPage(p);
    await fetchUsers({ toPage: p, toLimit: limit });
  };

  const handleLimitChange = async (val) => {
    // '' => fetch ALL (omit page & limit)
    const parsed = val === "" ? "" : Number(val);
    setLimit(parsed);
    setPage(1);
    await fetchUsers({ toPage: 1, toLimit: parsed });
  };

  const handleSearch = async () => {
    setPage(1);
    await fetchUsers({ toPage: 1, toLimit: limit });
  };

  const visibleUsers = useMemo(() => {
    try {
      if (!Array.isArray(users)) return [];
      if (!roleFilter || typeof roleFilter !== "string") return users;

      return users.filter((u) => {
        if (!u || typeof u !== "object") return false;
        const userRole = u.role_name || "";
        return (
          typeof userRole === "string" &&
          userRole.toLowerCase() === roleFilter.toLowerCase()
        );
      });
    } catch (error) {
      console.error("Error filtering users:", error);
      return Array.isArray(users) ? users : [];
    }
  }, [users, roleFilter]);

  const showingStart =
    typeof limit === "number" && limit > 0
      ? (page - 1) * limit + (visibleUsers.length ? 1 : 0)
      : visibleUsers.length
        ? 1
        : 0;
  const showingEnd =
    typeof limit === "number" && limit > 0
      ? (page - 1) * limit + visibleUsers.length
      : visibleUsers.length;

  const submitUser = async () => {
    try {
      const res = await fetch(`${API_URL}/v1/hris/user/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok || !data?.success)
        throw new Error(data?.message || "Failed to add user");
      toast.success("User added successfully");
      setShowModal(false);
      setFormData({
        username: "",
        password: "",
        name: "",
        user_role: "",
        email: "",
        mobile: "",
        isInternalExternal: "No",
        user_type: "user",
      });
      await fetchUsers({ toPage: 1, toLimit: limit });
      setPage(1);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Something went wrong");
    }
  };

  return (

    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >

      <ErrorBoundary>
        <div className="font-montserrat">
          <p className="text-[25px] mt-5">Settings / User Management</p>

          <div className="bg-white p-2 rounded-md mt-5 shadow-md">
            {/* Filters */}
            <p className="text-[18px]">Filter</p>
            <div className="flex flex-wrap gap-4 text-gray-700 my-5">
              {/* Employee No (server-side employee_no) */}
              <input
                placeholder="Employee No (e.g., EMP03438)"
                className="border border-gray-300 rounded-md p-2 w-[220px]"
                value={employeeNoFilter}
                onChange={(e) => setEmployeeNoFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />

              {/* Role (client-side filter) */}
              <select
                className="border border-gray-300 rounded-md p-2 w-[220px]"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.role_name}>
                    {r.role_name}
                  </option>
                ))}
              </select>

              {/* Limit selector drives server-side pagination; '' = ALL */}
              <select
                className="border border-gray-300 rounded-md p-2 w-[140px]"
                value={limit === "" ? "" : String(limit)}
                onChange={(e) => handleLimitChange(e.target.value)}
              >
                <option value="10">Limit: 10</option>
                <option value="20">Limit: 20</option>
                <option value="50">Limit: 50</option>
                <option value="">All</option>
              </select>

              <button
                onClick={handleSearch}
                className="bg-blue-500 text-white px-4 py-2 rounded-md"
              >
                Search
              </button>
              <button
                onClick={async () => {
                  setEmployeeNoFilter("");
                  setRoleFilter("");
                  setPage(1);
                  setLimit(10);
                  await fetchUsers({ toPage: 1, toLimit: 10 });
                }}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Reset
              </button>
            </div>

            <div className="flex justify-end mb-5">
              <button
                onClick={() => setShowModal(true)}
                className="bg-blue-500 text-white cursor-pointer p-2 rounded-md hover:bg-blue-400 delay-100"
              >
                Add User
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-gray-500 bg-gray-100 uppercase text-sm">
                  <tr>
                    <th className="p-3">Employee No</th>
                    <th className="p-3">Employee Name</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Role</th>
                    <th className="p-3">User Type</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="p-4 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : visibleUsers.length ? (
                    visibleUsers.map((u, i) => (
                      <tr
                        key={`${u.employee_no || "row"}-${i}`}
                        className="border-b border-gray-200 hover:bg-gray-50"
                      >
                        <td className="p-3 py-2">{u.employee_no || "-"}</td>
                        <td className="p-3 py-2">{u.name || "-"}</td>
                        <td className="p-3 py-2">{u.email || "-"}</td>
                        <td className="p-3 py-2">{u.role_name || "-"}</td>
                        <td className="p-3 py-2">{u.user_type || "-"}</td>

                        <td className="p-3 py-2">
                          {!permissionsLoading &&
                            hasPermission &&
                            hasPermission(10034) && (
                              <button
                                onClick={() => {
                                  try {
                                    if (u?.employee_no) {
                                      navigate(`/edit-user/${u.employee_no}`);
                                    }
                                  } catch (error) {
                                    console.error("Navigation error:", error);
                                    toast.error("Error navigating to edit user");
                                  }
                                }}
                                className="bg-gray-100 border border-gray-400 p-2 rounded hover:text-red-500"
                              >
                                <CiEdit size={15} />
                              </button>
                            )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-gray-500">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination (server-side) - hidden when limit === '' (ALL) */}
            {limit !== "" && (
              <div className="flex justify-between items-center mt-4 text-sm text-gray-500 mx-5">
                <span>
                  {total > 0
                    ? `Showing ${showingStart} to ${showingEnd} of ${total} entries`
                    : "Showing 0 entries"}
                </span>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => {
                    const isActive = page === i + 1;
                    return (
                      <button
                        key={i + 1}
                        className={`px-3 py-1 border rounded-md ${isActive
                          ? "bg-blue-500 text-white"
                          : "hover:bg-gray-100"
                          }`}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Add User Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
              <div className="bg-white p-6 rounded shadow-lg w-2/3 relative">
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-2 right-2 text-gray-500 text-xl"
                >
                  &times;
                </button>
                <h2 className="text-xl font-bold mb-4">Add User</h2>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Username
                    </label>
                    <input
                      placeholder="EMP03477"
                      value={formData.username}
                      onChange={(e) => handleForm("username", e.target.value)}
                      className="border p-2 rounded w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="text"
                      placeholder="******"
                      value={formData.password}
                      onChange={(e) => handleForm("password", e.target.value)}
                      className="border p-2 rounded w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      placeholder="Chalanka Kodikara"
                      value={formData.name}
                      onChange={(e) => handleForm("name", e.target.value)}
                      className="border p-2 rounded w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      User Role
                    </label>
                    <select
                      value={formData.user_role}
                      onChange={(e) => handleForm("user_role", e.target.value)}
                      className="border p-2 rounded w-full"
                    >
                      <option value="">-- Select Role --</option>
                      {roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.role_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="user@example.com"
                      value={formData.email}
                      onChange={(e) => handleForm("email", e.target.value)}
                      className="border p-2 rounded w-full"
                    />
                  </div>


                  <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                    Save
                  </button>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Mobile
                    </label>
                    <input
                      placeholder="0771234567"
                      value={formData.mobile}
                      onChange={(e) => handleForm("mobile", e.target.value)}
                      className="border p-2 rounded w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Is Internal / External
                    </label>
                    <div className="flex gap-4 mt-1">
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          value="Yes"
                          checked={formData.isInternalExternal === "Yes"}
                          onChange={(e) =>
                            handleForm("isInternalExternal", e.target.value)
                          }
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          value="No"
                          checked={formData.isInternalExternal === "No"}
                          onChange={(e) =>
                            handleForm("isInternalExternal", e.target.value)
                          }
                        />
                        No
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      User Type
                    </label>
                    <select
                      value={formData.user_type}
                      onChange={(e) => handleForm("user_type", e.target.value)}
                      className="border p-2 rounded w-full"
                    >
                      <option value="superadmin">Super Admin</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={submitUser}
                    className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-400"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </ErrorBoundary>
    </motion.div>

  );
};

export default ViewUserManagement;
